/**
 * Book ID Resolver - converts ref_id to UUID at render time.
 * Similar pattern to S3Image presigned URL resolution.
 *
 * The system uses two book ID formats:
 * - ref_id (integer like "1") - Used for agent tools (simpler for LLMs)
 * - UUID (like "3d1a7461-...") - Used in URLs (what frontend expects)
 *
 * This utility resolves ref_id to UUID for page links in chat messages.
 */
import { API_BASE_URL, tenantHeaders } from '../config/api';

// Cache for ref_id → UUID mappings (persists across component renders)
const refIdToUuidCache = new Map<string, string>();

// Track in-flight requests to prevent duplicate concurrent API calls
const pendingRequests = new Map<string, Promise<string | null>>();

/**
 * Check if a bookId is a ref_id (integer) vs UUID
 * ref_id is purely digits, UUID contains letters and dashes
 */
export function isRefId(bookId: string): boolean {
  return /^\d+$/.test(bookId);
}

/**
 * Resolve a ref_id to UUID. Returns cached value if available.
 *
 * @param refId - The integer ref_id as string (e.g., "1")
 * @returns Promise resolving to UUID string, or null if not found
 */
export async function resolveRefIdToUuid(refId: string): Promise<string | null> {
  // Check cache first
  if (refIdToUuidCache.has(refId)) {
    return refIdToUuidCache.get(refId)!;
  }

  // Check if request is already in flight - return same promise to prevent duplicates
  if (pendingRequests.has(refId)) {
    return pendingRequests.get(refId)!;
  }

  // Create and track the request
  const request = (async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        `${API_BASE_URL}/api/books/resolve/${refId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...tenantHeaders(),
          },
        }
      );

      if (!response.ok) {
        console.warn(`Failed to resolve ref_id ${refId}: ${response.statusText}`);
        return null;
      }

      const data = await response.json();
      const uuid = data.uuid;

      // Cache the result
      refIdToUuidCache.set(refId, uuid);

      return uuid;
    } catch (err) {
      console.error('Error resolving ref_id to UUID:', err);
      return null;
    } finally {
      // Clean up pending request
      pendingRequests.delete(refId);
    }
  })();

  pendingRequests.set(refId, request);
  return request;
}

/**
 * Pre-populate cache from books list (optimization)
 * Call this when books are loaded to avoid individual API calls
 *
 * @param refId - The ref_id (can be number or string)
 * @param uuid - The UUID string
 */
export function cacheBookMapping(refId: number | string, uuid: string): void {
  refIdToUuidCache.set(String(refId), uuid);
}

/**
 * Clear the cache (for logout/session cleanup)
 */
export function clearBookIdCache(): void {
  refIdToUuidCache.clear();
}

/**
 * Get cache size (for debugging)
 */
export function getBookIdCacheSize(): number {
  return refIdToUuidCache.size;
}
